import { getAuditTrailByid } from '../../../redux/auditTrailSlice'
import { openCopyReplace } from '../../../redux/previewSlice'
import {
  mergeAuditVersionContent,
  mergeAuditTrailRowVersionContentDeep,
  resolveAuditTrailProcedureIdentity,
  enrichProcedureIdentityFromAuditSnapshots,
} from './auditTrailProcedureIdentity'
import { resolveProcedureIdsFromPartRevisionLabel } from './resolveProcedureIdsFromPartRevisionLabel'

/**
 * When preview-current-node (or similar) has already resolved the procedure tree node from
 * snapshot, prefer those ids over sparse audit version_content.
 */
export function patchSourceNodeFromResolved(sourceNode, resolved) {
  if (!resolved || !resolved.type) return sourceNode
  const next = { ...sourceNode }
  if (resolved.type === 'section' && resolved.section) {
    const s = resolved.section
    const tid = s.tree_node_id || s.detail?.procedure_tree_ref
    if (tid) {
      next.nodeId = tid
      next.sectionId = tid
    }
    next.nodeType = 'section'
    return next
  }
  if (resolved.type === 'step' && resolved.section && resolved.stepIndex >= 0) {
    const st = resolved.section.steps?.[resolved.stepIndex]
    if (st) {
      const sid = st.tree_node_id || st.step_id_ref
      if (sid) {
        next.nodeId = sid
        next.stepId = sid
      }
      const secTid = resolved.section.tree_node_id
      if (secTid) next.sectionId = secTid
      next.nodeType = 'step'
      // Add section and step numbers from resolved data
      if (typeof resolved.sectionIndex === 'number' && resolved.sectionIndex >= 0) {
        next.globalSectionNumber = resolved.sectionIndex + 1
      }
      if (typeof resolved.stepIndex === 'number' && resolved.stepIndex >= 0) {
        next.globalStepNumber = resolved.stepIndex + 1
      }
    }
    return next
  }
  if (resolved.type === 'substep' && resolved.section && resolved.stepIndex >= 0) {
    const st = resolved.section.steps?.[resolved.stepIndex]
    const ss = st?.substeps?.[resolved.substepIndex]
    if (ss && st) {
      const subId = ss.tree_node_id || ss.substep_id_ref
      if (subId) {
        next.nodeId = subId
        next.substepId = subId
      }
      const stepId = st.tree_node_id || st.step_id_ref
      if (stepId) next.stepId = stepId
      const secTid = resolved.section.tree_node_id
      if (secTid) next.sectionId = secTid
      next.nodeType = 'substep'
      // Add section and step numbers from resolved data
      if (typeof resolved.sectionIndex === 'number' && resolved.sectionIndex >= 0) {
        next.globalSectionNumber = resolved.sectionIndex + 1
      }
      if (typeof resolved.stepIndex === 'number' && resolved.stepIndex >= 0) {
        next.globalStepNumber = resolved.stepIndex + 1
      }
    }
    return next
  }
  const leafTypes = ['attachment', 'video', 'rich-text', 'canva', 'canvas', 'form']
  if (leafTypes.includes(resolved.type) && resolved.childNode) {
    const c = resolved.childNode
    const d = c.detail || {}
    const id =
      c.tree_node_id ||
      d.rich_text_tree_node_id ||
      d.rich_text_node_id ||
      d.attachment_tree_node_id ||
      d.video_tree_node_id ||
      d.canvas_tree_node_id ||
      d.canva_tree_node_id ||
      d.form_tree_node_id
    if (id) next.nodeId = id
    if (resolved.type === 'canva' || resolved.type === 'canvas') {
      next.nodeType = 'canvas'
    } else {
      next.nodeType = resolved.type
    }
    if (resolved.section?.tree_node_id) next.sectionId = resolved.section.tree_node_id
    const step = resolved.section?.steps?.[resolved.stepIndex]
    if (step?.tree_node_id) next.stepId = step.tree_node_id
    if (typeof resolved.substepIndex === 'number' && resolved.substepIndex >= 0) {
      const sub = step?.substeps?.[resolved.substepIndex]
      if (sub?.tree_node_id) next.substepId = sub.tree_node_id
    }
    // Add section and step numbers from resolved data
    if (typeof resolved.sectionIndex === 'number' && resolved.sectionIndex >= 0) {
      next.globalSectionNumber = resolved.sectionIndex + 1
    }
    if (typeof resolved.stepIndex === 'number' && resolved.stepIndex >= 0) {
      next.globalStepNumber = resolved.stepIndex + 1
    }
    return next
  }
  return sourceNode
}

/**
 * Opens version-history copy (document picker + paste) from an audit trail row.
 * Shared by PreviewLayout and preview-current-node (CurrentNode).
 */
export async function dispatchAuditTrailNodeCopy({
  dispatch,
  auditTrailId,
  revData,
  nodeTitleOverride,
  resolved = null,
  snapshotEnrichedIdentity = null,
  breadcrumbText = null,
}) {
  if (!revData) return

  const versionContent =
    mergeAuditTrailRowVersionContentDeep(revData) ||
    mergeAuditVersionContent(revData) ||
    revData.version_content ||
    {}
  const action = [versionContent.action_to_show, revData.action]
    .filter((x) => x != null && String(x).trim() !== '')
    .join(' ')
    .toUpperCase()

  let nodeType = null
  let sourceNode = {}
  let sourceDocument = {}

  let identity = snapshotEnrichedIdentity ?? resolveAuditTrailProcedureIdentity(revData)
  if (
    !identity.partId ||
    !identity.revisionId ||
    !identity.partNumber ||
    !identity.revision
  ) {
    identity = await enrichProcedureIdentityFromAuditSnapshots(revData, identity)
  }
  const pr = revData.procedure_revision
  const finalPartNumber = identity.partNumber || pr?.part_number || pr?.partNumber || ''
  const finalRevision =
    (identity.revision ||
      pr?.revision ||
      pr?.rev ||
      pr?.part_rev ||
      pr?.label) ??
    ''
  const finalPartId =
    identity.partId || pr?.part_id_ref || pr?.partId || pr?.part_ref
  const finalRevisionId =
    identity.revisionId ||
    pr?.id ||
    pr?.revision_id ||
    pr?.procedure_revision_id

  sourceDocument = {
    partId: finalPartId,
    revisionId: finalRevisionId,
    partNumber: finalPartNumber,
    revision: finalRevision,
    type:
      versionContent.item_code_type ||
      versionContent.type ||
      pr?.item_code_type ||
      'Procedure',
    description:
      versionContent.partDescription ||
      versionContent.description ||
      versionContent.short_description ||
      pr?.description ||
      '',
  }

  let copyPartId = sourceDocument.partId
  let copyRevisionId = sourceDocument.revisionId
  let copyPartNumber = sourceDocument.partNumber
  let copyRevision = sourceDocument.revision

  if (
    (!copyPartId ||
      !copyRevisionId ||
      copyRevision == null ||
      copyPartNumber === '') &&
    auditTrailId
  ) {
    const resp = await dispatch(getAuditTrailByid({ id: auditTrailId }))
    const isFulfilled = resp?.type?.endsWith?.('/fulfilled')
    const payload = isFulfilled ? resp?.payload : null
    const fresh = payload && (payload.results ?? payload.data ?? payload)
    if (
      fresh &&
      typeof fresh === 'object' &&
      !('message' in fresh && 'stack' in fresh)
    ) {
      let freshIdentity = resolveAuditTrailProcedureIdentity(fresh)
      if (
        !freshIdentity.partId ||
        !freshIdentity.revisionId ||
        !freshIdentity.partNumber ||
        !freshIdentity.revision
      ) {
        freshIdentity = await enrichProcedureIdentityFromAuditSnapshots(
          fresh,
          freshIdentity,
        )
      }
      const prFresh = fresh.procedure_revision
      copyPartId =
        copyPartId ||
        freshIdentity.partId ||
        prFresh?.part_id_ref ||
        prFresh?.partId ||
        prFresh?.part_ref
      copyRevisionId =
        copyRevisionId ||
        freshIdentity.revisionId ||
        prFresh?.id ||
        prFresh?.revision_id ||
        prFresh?.procedure_revision_id
      copyPartNumber =
        copyPartNumber ||
        freshIdentity.partNumber ||
        prFresh?.part_number ||
        prFresh?.partNumber ||
        ''
      copyRevision =
        copyRevision ||
        freshIdentity.revision ||
        prFresh?.revision ||
        prFresh?.rev ||
        prFresh?.part_rev ||
        prFresh?.label ||
        ''
    }
  }

  if (
    (!copyPartId || !copyRevisionId) &&
    copyPartNumber &&
    copyRevision != null
  ) {
    const ids = await resolveProcedureIdsFromPartRevisionLabel(dispatch, {
      partId: copyPartId,
      revisionId: copyRevisionId,
      partNumber: copyPartNumber,
      revision: copyRevision,
    })
    copyPartId = copyPartId || ids.partId
    copyRevisionId = copyRevisionId || ids.revisionId
  }

  sourceDocument = {
    ...sourceDocument,
    partId: copyPartId,
    revisionId: copyRevisionId,
    partNumber: copyPartNumber,
    revision: copyRevision,
    /** Audit row version — copy APIs must base content on this, not latest procedure state */
    version_id_ref:
      revData.version_id ??
      versionContent.version_id ??
      versionContent.version_id_ref,
  }

  if (action.includes('SECTION')) {
    nodeType = 'section'
    let sectionTitle = 'Section'
    if (versionContent.breadcrumb_text) {
      const breadcrumbParts = versionContent.breadcrumb_text.split(' > ')
      const lastPart = breadcrumbParts[breadcrumbParts.length - 1]
      if (lastPart && lastPart.match(/^Section\s+\d+$/i)) sectionTitle = lastPart
    } else if (versionContent.sibling_order) {
      sectionTitle = `Section ${versionContent.sibling_order}`
    } else if (
      versionContent.pageTitle &&
      versionContent.pageTitle.match(/^Section\s+\d+$/i)
    ) {
      sectionTitle = versionContent.pageTitle
    }
    const sectionProcedureId =
      versionContent.section_tree_node_id ||
      versionContent.section_id_ref ||
      versionContent.tree_node_id ||
      versionContent.preview_tree_node_id ||
      versionContent.procedure_tree_ref
    sourceNode = {
      nodeId: sectionProcedureId,
      nodeTitle: sectionTitle,
      nodeType: 'section',
      sectionId: sectionProcedureId,
    }
  } else if (action.includes('STEP') && !action.includes('SUBSTEP')) {
    nodeType = 'step'
    const stepProcedureId =
      versionContent.step_tree_node_id ||
      versionContent.step_id_ref ||
      versionContent.tree_node_id ||
      versionContent.preview_tree_node_id
    const sectionForStepId =
      versionContent.section_tree_node_id ||
      versionContent.section_id_ref ||
      versionContent.procedure_tree_ref
    // Extract section and step numbers from action_to_show
    const extractStepNumbers = () => {
      const actionText = versionContent.action_to_show || '';
      const breadcrumbText = versionContent.breadcrumb_text || '';
      
      // Extract section number
      const sectionMatch = /section\s+(\d+)/i.exec(actionText);
      const breadcrumbSectionMatch = /section\s+(\d+)/i.exec(breadcrumbText.split(' > ').pop());
      const sectionNumber = sectionMatch ? parseInt(sectionMatch[1], 10) : 
                           breadcrumbSectionMatch ? parseInt(breadcrumbSectionMatch[1], 10) : null;
      
      // Extract step number
      const stepMatch = /step\s+(\d+)/i.exec(actionText);
      const stepNumber = stepMatch ? parseInt(stepMatch[1], 10) : null;
      
      return { sectionNumber, stepNumber };
    };

    const { sectionNumber, stepNumber } = extractStepNumbers();

    sourceNode = {
      nodeId: stepProcedureId,
      nodeTitle: versionContent.stepTitle || versionContent.pageTitle || 'Step',
      nodeType: 'step',
      sectionId: sectionForStepId,
      stepId: stepProcedureId,
      globalSectionNumber: sectionNumber,
      globalStepNumber: stepNumber,
    }
  } else if (action.includes('SUBSTEP')) {
    nodeType = 'substep'
    const substepProcedureId =
      versionContent.sub_step_tree_node_id ||
      versionContent.substep_tree_node_id ||
      versionContent.tree_node_id ||
      versionContent.preview_tree_node_id
    const sectionForSubstepId =
      versionContent.section_tree_node_id ||
      versionContent.section_id_ref ||
      versionContent.procedure_tree_ref
    const stepForSubstepId =
      versionContent.step_id_ref || versionContent.step_tree_node_id
    // Extract section and step numbers from action_to_show for substep
    const extractSubstepNumbers = () => {
      const actionText = versionContent.action_to_show || '';
      const breadcrumbText = versionContent.breadcrumb_text || '';
      
      // Extract section number
      const sectionMatch = /section\s+(\d+)/i.exec(actionText);
      const breadcrumbSectionMatch = /section\s+(\d+)/i.exec(breadcrumbText.split(' > ').pop());
      const sectionNumber = sectionMatch ? parseInt(sectionMatch[1], 10) : 
                           breadcrumbSectionMatch ? parseInt(breadcrumbSectionMatch[1], 10) : null;
      
      // Extract step number
      const stepMatch = /step\s+(\d+)/i.exec(actionText);
      const stepNumber = stepMatch ? parseInt(stepMatch[1], 10) : null;
      
      return { sectionNumber, stepNumber };
    };

    const { sectionNumber: substepSectionNumber, stepNumber: substepStepNumber } = extractSubstepNumbers();

    sourceNode = {
      nodeId: substepProcedureId,
      nodeTitle: versionContent.substepTitle || versionContent.pageTitle || 'Substep',
      nodeType: 'substep',
      sectionId: sectionForSubstepId,
      stepId: stepForSubstepId,
      substepId: substepProcedureId,
      globalSectionNumber: substepSectionNumber,
      globalStepNumber: substepStepNumber,
    }
  } else {
    const nodeTypes = [
      ['RICHTEXT', 'rich-text'],
      ['RICH_TEXT', 'rich-text'],
      ['RICH-TEXT', 'rich-text'],
      ['ATTACHMENT', 'attachment'],
      ['VIDEO', 'video'],
      ['CANVAS', 'canvas'],
      ['CANVA', 'canvas'],
      ['FORM_COPIED', 'form'],
      ['FORM_MOVED', 'form'],
      ['FORM_EDITED', 'form'],
      ['FORM_DELETED', 'form'],
      ['FORM_', 'form'],
    ]
    for (const [key, value] of nodeTypes) {
      if (action.includes(key)) {
        nodeType = value
        break
      }
    }
    if (!nodeType) {
      const raw = (versionContent.type || versionContent.element_type || '')
        .toLowerCase()
        .replace(/_/g, '-')
      const fromType = {
        attachment: 'attachment',
        video: 'video',
        'rich-text': 'rich-text',
        richtext: 'rich-text',
        form: 'form',
        canvas: 'canvas',
        canva: 'canvas',
      }[raw]
      if (fromType) nodeType = fromType
    }

    // For video nodes, extract breadcrumb information from available sources
    if (nodeType === 'video') {
      // Extract breadcrumb information from various sources
      const actionText = versionContent.action_to_show || '';
      const versionBreadcrumbText = versionContent.breadcrumb_text || '';
      const shortDescription = versionContent.short_description || '';
      const revBreadcrumb = revData?.breadcrumb_text || revData?.breadcrumb;
      const identityBreadcrumb = snapshotEnrichedIdentity?.breadcrumb_text || snapshotEnrichedIdentity?.breadcrumb;
      const passedBreadcrumbText = breadcrumbText; // This comes from PreviewLayout
      
      // Try to extract section number from any available source
      let extractedSectionNumber = null;
      let extractedStepNumber = null;
      let substepInfo = { isSubstep: false };
      
      // Check all breadcrumb sources for section info
      const allBreadcrumbSources = [actionText, versionBreadcrumbText, shortDescription, revBreadcrumb, identityBreadcrumb, passedBreadcrumbText];
      for (const source of allBreadcrumbSources) {
        if (source) {
          const sectionMatch = /section\s+(\d+)/i.exec(source);
          if (sectionMatch) {
            extractedSectionNumber = parseInt(sectionMatch[1], 10);
            break;
          }
        }
      }
      
      // Check all breadcrumb sources for step info
      for (const source of allBreadcrumbSources) {
        if (source) {
          const stepMatch = /step\s+(\d+)/i.exec(source);
          if (stepMatch) {
            extractedStepNumber = parseInt(stepMatch[1], 10);
            break;
          }
        }
      }
      
      // Check all breadcrumb sources for substep info
      for (const source of allBreadcrumbSources) {
        if (source) {
          const parts = source.split(' > ');
          for (let i = 0; i < parts.length; i++) {
            const part = parts[i];
            const substepMatch = /substep\s+(\d+)([A-Z])/i.exec(part);
            if (substepMatch) {
              const stepNum = parseInt(substepMatch[1], 10);
              const substepLetter = substepMatch[2];
              // Find the section before this substep
              let sectionNum = null;
              for (let j = i - 1; j >= 0; j--) {
                const sectionMatch = /section\s+(\d+)/i.exec(parts[j]);
                if (sectionMatch) {
                  sectionNum = parseInt(sectionMatch[1], 10);
                  break;
                }
              }
              
              substepInfo = { sectionNumber: sectionNum, stepNumber: stepNum, isSubstep: true, substepLetter };
              break;
            }
          }
          if (substepInfo.isSubstep) break;
        }
      }
      
      // Use substep info if available, otherwise fall back to regular extraction
      let finalSectionNumber = extractedSectionNumber;
      let finalStepNumber = extractedStepNumber;
      let substepIndex = null;
      let substepLetter = null;

      if (substepInfo.isSubstep) {
        finalSectionNumber = substepInfo.sectionNumber;
        finalStepNumber = substepInfo.stepNumber;
        substepLetter = substepInfo.substepLetter;
        // Convert substep letter to index (A=0, B=1, etc.)
        substepIndex = substepLetter.charCodeAt(0) - 65;
      }

      // For video nodes, try multiple sources to get the nodeId
      let nodeIdForVideo = null;
      if (nodeType === 'video') {
        nodeIdForVideo = 
          versionContent.video_tree_node_id ||
          versionContent.canvas_tree_node_id ||
          versionContent.canva_tree_node_id ||
          versionContent.rich_text_node_id ||
          versionContent.rich_text_tree_node_id ||
          versionContent.attachment_tree_node_id ||
          versionContent.form_tree_node_id ||
          versionContent.tree_node_id ||
          versionContent.preview_tree_node_id ||
          versionContent.element_id;
        
        // If still no nodeId, try to get it from global video node data
        if (!nodeIdForVideo && window.lastExtractedBreadcrumbData?.videoNode?.tree_node_id) {
          nodeIdForVideo = window.lastExtractedBreadcrumbData.videoNode.tree_node_id;
        }
        
        // If still no nodeId, try to get it from videos array in versionContent
        if (!nodeIdForVideo && versionContent.videos && Array.isArray(versionContent.videos)) {
          for (const video of versionContent.videos) {
            if (video.video_tree_node_ref) {
              nodeIdForVideo = video.video_tree_node_ref;
              break;
            }
          }
        }
      }

      sourceNode = {
        nodeId: nodeType === 'video' ? nodeIdForVideo : (
          versionContent.video_tree_node_id ||
          versionContent.canvas_tree_node_id ||
          versionContent.canva_tree_node_id ||
          versionContent.rich_text_node_id ||
          versionContent.rich_text_tree_node_id ||
          versionContent.attachment_tree_node_id ||
          versionContent.form_tree_node_id ||
          versionContent.tree_node_id ||
          versionContent.preview_tree_node_id ||
          versionContent.element_id
        ),
        nodeTitle:
          versionContent.name ||
          versionContent.elementTitle ||
          versionContent.pageTitle ||
          'Element',
        nodeType: nodeType,
        sectionId:
          versionContent.section_tree_node_id ||
          versionContent.section_id_ref ||
          versionContent.procedure_tree_ref,
        stepId:
          versionContent.step_id_ref ||
          versionContent.step_tree_node_id ||
          versionContent.preview_tree_node_id,
        substepId:
          versionContent.substep_id_ref ||
          versionContent.sub_step_tree_node_id ||
          versionContent.substep_tree_node_id,
        globalSectionNumber: finalSectionNumber,
        globalStepNumber: finalStepNumber,
        substepIndex: substepIndex,
        isSubstepChild: substepInfo.isSubstep,
      }

    } else {
      // For non-video nodes, use the original logic
      sourceNode = {
        nodeId:
          versionContent.video_tree_node_id ||
          versionContent.canvas_tree_node_id ||
          versionContent.canva_tree_node_id ||
          versionContent.rich_text_node_id ||
          versionContent.rich_text_tree_node_id ||
          versionContent.attachment_tree_node_id ||
          versionContent.form_tree_node_id ||
          versionContent.tree_node_id ||
          versionContent.preview_tree_node_id ||
          versionContent.element_id,
        nodeTitle:
          versionContent.name ||
          versionContent.elementTitle ||
          versionContent.pageTitle ||
          'Element',
        nodeType: nodeType,
        sectionId:
          versionContent.section_tree_node_id ||
          versionContent.section_id_ref ||
          versionContent.procedure_tree_ref,
        stepId:
          versionContent.step_id_ref ||
          versionContent.step_tree_node_id ||
          versionContent.preview_tree_node_id,
        substepId:
          versionContent.substep_id_ref ||
          versionContent.sub_step_tree_node_id ||
          versionContent.substep_tree_node_id,
      }
    }
  }

  sourceNode = {
    ...sourceNode,
    nodeTitle: nodeTitleOverride || sourceNode.nodeTitle,
  }

  if (resolved) {
    sourceNode = patchSourceNodeFromResolved(sourceNode, resolved)
  }

  dispatch(
    openCopyReplace({
      copyMode: 'node',
      sourceDocument,
      sourceNode,
      isVersionHistory: true,
      auditTrailId,
      versionId: revData.version_id,
      breadcrumbText,
    }),
  )
}
